import { Fragment, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { SimpleListing } from '@th/shared/src/types/SimpleListing';
import formatRepliersImageSource from '@th/shared/src/helpers/formatRepliersImageSource';
import ListingImagesSingleImageFullScreenModal from './ListingImagesSingleImageFullScreenModal';
import { FULL_IMAGE_RESOLUTION } from '@th/frontend-shared/src/constants/images';
import SvgIcon from '../icons/SvgIcon';

type Props = {
  listing: SimpleListing;
  isOpen: boolean;
  onClose: () => void;
};

export default function ListingImagesMultipleImageFullScreenModal({
  listing,
  isOpen,
  onClose,
}: Props) {
  const firstImageRef = useRef(null);
  const imageUrls = listing.images.map(image => {
    return formatRepliersImageSource(image, 'large');
  });
  const [isSingleImageViewOpen, setIsSingleImageViewOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  function showSingleImage(index: number) {
    setSelectedImageIndex(index);
    setIsSingleImageViewOpen(true);
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-30"
        onClose={onClose}
        initialFocus={firstImageRef}
      >
        <div className="fixed inset-0 z-10">
          <div className="flex min-h-full justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="flex flex-col relative transform bg-white transition-all h-screen w-screen">
                <header>
                  <button onClick={onClose} className="p-3 hover:text-primary">
                    <SvgIcon
                      name={'HiChevronLeft'}
                      className="h-8 md:h-10 w-8 md:w-10"
                    />
                  </button>
                </header>
                <div className="flex flex-col flex-1 overflow-y-auto">
                  <div className="flex flex-col items-center gap-2 md:gap-3">
                    {imageUrls.map((image, i) => {
                      return (
                        <button
                          onClick={() => showSingleImage(i)}
                          key={image.uri}
                        >
                          <img
                            ref={i === 0 ? firstImageRef : null}
                            style={{ maxHeight: '40em', maxWidth: '64em' }}
                            src={image.uri}
                            alt={`${listing.address.formattedAddressShortOneLiner()}`}
                            className="object-cover align-bottom aspect-3/2 w-full"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
        <ListingImagesSingleImageFullScreenModal
          imageUrls={imageUrls}
          isOpen={isSingleImageViewOpen}
          onClose={() => setIsSingleImageViewOpen(false)}
          initialImageIndex={selectedImageIndex}
        />
      </Dialog>
    </Transition.Root>
  );
}
